import React, { useState } from "react";
import { useGame } from "@/contexts/GameContext";
import useTabMusic from "@/hooks/useTabMusic";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Users, Package, Sparkles, Shield, Sword, Heart, User, BarChart3, Coins } from "lucide-react";
import { toast } from "sonner";
import GameCard from "./GameCard";
import { t } from "@/utils/i18n";

const groupCards = (cardsList) => {
  if (!cardsList) return [];
  const groups = {};
  cardsList.forEach(card => {
    const key = `${card.name}_${card.level || 1}_${card.rarity || 'common'}_${!!card.foil}`;
    if (!groups[key]) {
      groups[key] = {
        ...card,
        quantity: 1,
        instances: [card],
      };
    } else {
      groups[key].quantity += 1;
      groups[key].instances.push(card);
    }
  });
  return Object.values(groups);
};

export default function Inventory() {
  const { game, equipItem, unequipItem } = useGame();
  const [activeTab, setActiveTab] = useState("allies");
  useTabMusic('inventory');

  if (!game) return null;

  const lang = game.language || "en";
  const rawInventory = game.inventory || { allies: [], objects: [], abilities: [] };
  const character = game.character;
  const equipped = character.equipped || {};
  const inBattle = game.battle_state && game.battle_state.active;

  const groupedInventory = {
    allies: groupCards(rawInventory.allies),
    objects: groupCards(rawInventory.objects),
    abilities: groupCards(rawInventory.abilities),
  };

  const totalItemsCount = (rawInventory.allies?.length || 0) + 
                          (rawInventory.objects?.length || 0) + 
                          (rawInventory.abilities?.length || 0);

  const handleEquip = async (cardId, slot) => {
    try {
      await equipItem(game.id, cardId, slot);
      toast.success(t(lang, 'equip') + "!");
    } catch {
      toast.error("Failed to equip");
    }
  };

  const handleUnequip = async (cardId, slot) => {
    try {
      await unequipItem(game.id, cardId, slot);
      toast.success(t(lang, 'unequip'));
    } catch {
      toast.error("Failed to unequip");
    }
  };

  return (
    <div className="py-2 sm:py-6 px-2 sm:px-4" data-testid="inventory-view">
      <div className="max-w-5xl mx-auto">
        <div className="hud-container p-4 sm:p-6 relative">
          
          {/* Responsive HUD Corner SVGs */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 left-0 text-[#cfb53b] hud-glow-gold pointer-events-none">
            <path d="M2 32V12L12 2H32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M5 32V14L14 5H32" stroke="#836242" strokeWidth="1" strokeLinecap="round" className="opacity-60" />
          </svg>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 right-0 text-[#cfb53b] hud-glow-gold pointer-events-none">
            <path d="M30 32V12L20 2H0" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M27 32V14L18 5H0" stroke="#836242" strokeWidth="1" strokeLinecap="round" className="opacity-60" />
          </svg>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 left-0 text-[#cfb53b] hud-glow-gold pointer-events-none">
            <path d="M2 0V20L12 30H32" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M5 0V18L14 27H32" stroke="#836242" strokeWidth="1" strokeLinecap="round" className="opacity-60" />
          </svg>
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute bottom-0 right-0 text-[#cfb53b] hud-glow-gold pointer-events-none">
            <path d="M30 0V20L20 30H0" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M27 0V18L18 27H0" stroke="#836242" strokeWidth="1" strokeLinecap="round" className="opacity-60" />
          </svg>

          {/* Border connector lines */}
          <div className="absolute top-0 left-8 right-8 border-t-2 border-[#cfb53b]/80 hud-glow-gold pointer-events-none" />
          <div className="absolute bottom-0 left-8 right-8 border-b-2 border-[#cfb53b]/80 hud-glow-gold pointer-events-none" />
          <div className="absolute left-0 top-8 bottom-8 border-l-2 border-[#cfb53b]/80 hud-glow-gold pointer-events-none" />
          <div className="absolute right-0 top-8 bottom-8 border-r-2 border-[#cfb53b]/80 hud-glow-gold pointer-events-none" />

          {/* Header Panel */}
          <div className="flex items-center justify-between mb-4 mt-2 px-2 sm:px-4">
            <h2 className="font-heading text-lg sm:text-2xl text-[#cfb53b] tracking-widest uppercase flex items-center gap-2">
              <Package size={20} className="text-[#cfb53b]" />
              {t(lang, 'inventory')}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 relative z-10 px-1">
            
            {/* CHARACTER EQUIPMENT PANEL */}
            <div className="lg:col-span-1">
              <div className="hud-panel p-4 sm:p-5 h-full flex flex-col justify-between" data-testid="character-equipment">
                <div>
                  <h3 className="hud-panel-title text-sm sm:text-base mb-4 flex items-center gap-2 border-b border-[#cfb53b]/20 pb-2">
                    <User size={15} className="text-[#cfb53b]" /> {t(lang, 'equipment')}
                  </h3>

                  {/* Equipped Slots */}
                  <div className="grid grid-cols-3 sm:grid-cols-1 gap-3 mb-6">
                    {[
                      { slot: "weapon", label: t(lang, 'weapon'), defaultIcon: Sword },
                      { slot: "armor", label: t(lang, 'armor_slot'), defaultIcon: Shield },
                      { slot: "accessory", label: t(lang, 'accessory'), defaultIcon: Sparkles },
                    ].map(({ slot, label, defaultIcon: Icon }) => {
                      const item = equipped[slot];
                      return (
                        <div key={slot} className="hud-equip-tile rounded-lg p-2.5 flex flex-col justify-between min-h-[64px]" data-testid={`equip-slot-${slot}`}>
                          <div className="flex items-center justify-between border-b border-white/5 pb-1">
                            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-heading">{label}</span>
                            {item && (
                              <Button
                                data-testid={`unequip-${slot}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnequip(item.id, slot)}
                                className="text-[9px] text-red-400 hover:text-red-300 h-4 px-1.5 bg-red-950/40 hover:bg-red-900/60 rounded border border-red-500/30"
                              >
                                {t(lang, 'unequip')}
                              </Button>
                            )}
                          </div>
                          {item ? (
                            <div className="mt-1.5 flex items-center justify-between">
                              <p className="font-heading text-xs text-[#cfb53b] truncate max-w-[120px] sm:max-w-none">{item.name}</p>
                              <div className="flex gap-1.5">
                                {item.attack > 0 && <span className="text-[9px] font-heading text-red-400 bg-red-950/20 px-1 rounded">+{item.attack}⚔</span>}
                                {item.defense > 0 && <span className="text-[9px] font-heading text-blue-400 bg-blue-950/20 px-1 rounded">+{item.defense}🛡</span>}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 flex items-center gap-1.5 text-slate-500 italic">
                              <Icon size={12} className="opacity-60" />
                              <span className="text-[10px]">{t(lang, 'empty_slot')}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Stat Display Section */}
                <div>
                  <h3 className="hud-panel-title text-sm mb-3 flex items-center gap-2 border-b border-[#cfb53b]/20 pb-2">
                    <BarChart3 size={14} className="text-[#cfb53b]" /> Stats
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="hud-stat-cell rounded p-2 text-center">
                      <Heart size={13} className="mx-auto text-red-500 mb-1" />
                      <p className="text-[8px] uppercase tracking-wider text-slate-400">{t(lang, 'hp')}</p>
                      <p className="font-heading text-xs text-[#e2e8f0] font-bold">{character.hp}/{character.max_hp}</p>
                    </div>
                    <div className="hud-stat-cell rounded p-2 text-center">
                      <Sparkles size={13} className="mx-auto text-sky-400 mb-1" />
                      <p className="text-[8px] uppercase tracking-wider text-slate-400">{t(lang, 'mana')}</p>
                      <p className="font-heading text-xs text-[#e2e8f0] font-bold">{character.mana}/{character.max_mana}</p>
                    </div>
                    <div className="hud-stat-cell rounded p-2 text-center">
                      <Sword size={13} className="mx-auto text-amber-500 mb-1" />
                      <p className="text-[8px] uppercase tracking-wider text-slate-400">{t(lang, 'atk')}</p>
                      <p className="font-heading text-xs text-[#e2e8f0] font-bold">
                        {character.effective_attack ?? character.base_attack}
                        {character.equip_bonus_atk > 0 && (
                          <span className="text-[9px] text-[#cfb53b] ml-1">(+{character.equip_bonus_atk})</span>
                        )}
                      </p>
                    </div>
                    <div className="hud-stat-cell rounded p-2 text-center">
                      <Shield size={13} className="mx-auto text-blue-400 mb-1" />
                      <p className="text-[8px] uppercase tracking-wider text-slate-400">{t(lang, 'def')}</p>
                      <p className="font-heading text-xs text-[#e2e8f0] font-bold">
                        {character.effective_defense ?? character.base_defense}
                        {character.equip_bonus_def > 0 && (
                          <span className="text-[9px] text-blue-400 ml-1">(+{character.equip_bonus_def})</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CARD COLLECTION PANEL */}
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                
                <TabsList className="bg-black/50 border border-[#cfb53b]/20 p-1 w-full grid grid-cols-3 h-11 rounded-lg">
                  <TabsTrigger
                    value="allies"
                    data-testid="inventory-tab-allies"
                    className="font-heading text-xs sm:text-sm hud-tab-btn rounded-md h-9 data-[state=active]:font-bold"
                  >
                    <Users size={14} className="mr-1.5" />
                    {t(lang, 'allies')} ({groupedInventory.allies?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger
                    value="objects"
                    data-testid="inventory-tab-objects"
                    className="font-heading text-xs sm:text-sm hud-tab-btn rounded-md h-9 data-[state=active]:font-bold"
                  >
                    <Package size={14} className="mr-1.5" />
                    {t(lang, 'objects')} ({groupedInventory.objects?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger
                    value="abilities"
                    data-testid="inventory-tab-abilities"
                    className="font-heading text-xs sm:text-sm hud-tab-btn rounded-md h-9 data-[state=active]:font-bold"
                  >
                    <Sparkles size={14} className="mr-1.5" />
                    {t(lang, 'abilities')} ({groupedInventory.abilities?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="allies" className="mt-3">
                  {groupedInventory.allies?.length === 0 ? (
                    <EmptyState text={t(lang, 'no_allies')} icon={Users} />
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 stagger-children max-h-[360px] md:max-h-[540px] overflow-y-auto pr-1">
                      {groupedInventory.allies.map(card => (
                        <GameCard key={card.id} card={card} type="ally" inBattle={inBattle} variant="hud-slot" />
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="objects" className="mt-3">
                  {groupedInventory.objects?.length === 0 ? (
                    <EmptyState text={t(lang, 'no_objects')} icon={Package} />
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 stagger-children max-h-[360px] md:max-h-[540px] overflow-y-auto pr-1">
                      {groupedInventory.objects.map(card => (
                        <GameCard key={card.id} card={card} type="object" onEquip={handleEquip} inBattle={inBattle} variant="hud-slot" />
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="abilities" className="mt-3">
                  {groupedInventory.abilities?.length === 0 ? (
                    <EmptyState text={t(lang, 'no_abilities')} icon={Sparkles} />
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 stagger-children max-h-[360px] md:max-h-[540px] overflow-y-auto pr-1">
                      {groupedInventory.abilities.map(card => (
                        <GameCard key={card.id} card={card} type="ability" inBattle={inBattle} variant="hud-slot" />
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-[#cfb53b]/20 flex flex-col sm:flex-row items-center justify-between gap-3 px-2 sm:px-4 relative z-10">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-[10px] font-heading font-bold text-slate-400 tracking-wider">{t(lang, 'hold')}</span>
              <span className="text-[10px] font-heading font-bold text-[#cfb53b] shrink-0">
                {totalItemsCount}
              </span>
            </div>

            <div className="flex items-center gap-2 bg-[#cfb53b]/10 border border-[#cfb53b]/40 rounded-lg px-4 py-1.5 shadow-[0_0_12px_rgba(207,181,59,0.15)] shrink-0">
              <span className="text-[10px] font-heading font-bold text-[#cfb53b] tracking-wider uppercase">GOLD</span>
              <Coins size={14} className="text-[#cfb53b]" />
              <span className="font-heading text-sm text-[#cfb53b] font-bold">
                {character.gold?.toLocaleString() || "0"}
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function EmptyState({ text, icon: Icon }) {
  return (
    <div className="text-center py-12 border border-dashed border-[#2d3748] rounded-lg">
      <Icon size={40} className="mx-auto text-[#cfb53b]/20 mb-3" strokeWidth={1} />
      <p className="text-[#94a3b8] text-sm font-heading">{text}</p>
    </div>
  );
}
